import { useEffect, useRef, useState } from "react";

import { createXrInputController, isXrInputSupported } from "../modules/teleop-xr";
import { XR_SOURCE_ID } from "../modules/teleop-input";
import type { OperatorInputController } from "../modules/teleop-input";
import type { XrVideoSource, XrVideoDiagnostics } from "../modules/teleop-xr";

export interface UseTeleopXrResult {
  /** null while the async capability check is still in flight. */
  supported: boolean | null;
  active: boolean;
  arrangingWindows: boolean;
  videoDiagnostics: XrVideoDiagnostics[];
  tracking: { samples: number; head: boolean; hands: number; peakHands: number; sources: string[]; seenSources: string[] };
  /** Enter VR and make the XR controllers the active source. MUST be called from a user
   * gesture (button onClick) — WebXR requires transient activation. */
  enter: (mode?: "immersive-ar" | "immersive-vr", arrangeWindows?: boolean) => Promise<void>;
  exit: () => Promise<void>;
}

/**
 * Thin React adapter over teleop-xr's session controller: XR samples go to the operator
 * input controller (teleop-input), which streams them as `xr` operator-input frames
 * while the XR source is the active one. Session end (headset removed, "exit VR") is
 * reported back so the controller sends `send-stop` and switches control off.
 */
export function useTeleopXr(
  input: OperatorInputController,
  onStatus?: (text: string) => void,
  autoSelect = true,
  getVideoSources?: () => XrVideoSource[],
  videoLayoutKey?: string
): UseTeleopXrResult {
  const controllerRef = useRef(createXrInputController());
  const [supported, setSupported] = useState<boolean | null>(null);
  const [active, setActive] = useState(false);
  const [arrangingWindows, setArrangingWindows] = useState(false);
  const [videoDiagnostics, setVideoDiagnostics] = useState<XrVideoDiagnostics[]>([]);
  const [tracking, setTracking] = useState({
    samples: 0, head: false, hands: 0, peakHands: 0,
    sources: [] as string[], seenSources: [] as string[],
  });
  const sampleCount = useRef(0);
  const lastTrackingUpdate = useRef(0);
  const peakHands = useRef(0);
  const seenSources = useRef(new Map<string, string>());
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;

  useEffect(() => {
    let cancelled = false;
    void isXrInputSupported().then((ok) => {
      if (cancelled) return;
      setSupported(ok);
      input.setXrSupported(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [input]);

  useEffect(() => {
    const controller = controllerRef.current;
    return () => {
      void controller.stop();
    };
  }, []);

  const enter = (mode: "immersive-ar" | "immersive-vr" = "immersive-ar", arrangeWindows = false) => {
    if (arrangeWindows) input.stopRobot();
    setArrangingWindows(arrangeWindows);
    setVideoDiagnostics([]);
    sampleCount.current = 0;
    lastTrackingUpdate.current = 0;
    peakHands.current = 0;
    seenSources.current.clear();
    setTracking({ samples: 0, head: false, hands: 0, peakHands: 0, sources: [], seenSources: [] });
    return controllerRef.current.start({
      mode,
      getVideoSources,
      arrangeWindows,
      videoLayoutKey,
      onVideoDiagnostics: setVideoDiagnostics,
      onSample: (data) => {
        if (!arrangeWindows) input.pushXrSample(data);
        sampleCount.current += 1;
        const readyHands = data.hands.filter((hand) =>
          !hand.hand && hand.grip != null && hand.axes.length >= 4 && hand.buttons.length >= 6
        ).length;
        peakHands.current = Math.max(peakHands.current, readyHands);
        for (const hand of data.hands) {
          seenSources.current.set(hand.handedness,
            `${hand.handedness}: ${hand.profiles[0] ?? "без профиля"}, оси ${hand.axes.length}, кнопки ${hand.buttons.length}, захват ${hand.grip ? "есть" : "нет"}`
          );
        }
        const now = Date.now();
        if (now - lastTrackingUpdate.current >= 500) {
          lastTrackingUpdate.current = now;
          setTracking({
            samples: sampleCount.current,
            head: data.head != null,
            hands: readyHands,
            peakHands: peakHands.current,
            sources: data.hands.map((hand) =>
              `${hand.handedness}: ${hand.profiles[0] ?? "без профиля"}, оси ${hand.axes.length}, кнопки ${hand.buttons.length}, захват ${hand.grip ? "есть" : "нет"}`
            ),
            seenSources: Array.from(seenSources.current.values()),
          });
        }
      },
      onProfiles: (profiles) => input.setXrActive(true, profiles),
      onStatus: (text) => onStatusRef.current?.(text),
      onActiveChange: (isActive) => {
        setActive(isActive);
        if (!isActive) setArrangingWindows(false);
        input.setXrActive(isActive);
        if (isActive && autoSelect && !arrangeWindows) input.select(XR_SOURCE_ID);
      },
    }).catch((error: unknown) => { setArrangingWindows(false); throw error; });
  };

  const exit = (): Promise<void> => controllerRef.current.stop();

  return { supported, active, arrangingWindows, videoDiagnostics, tracking, enter, exit };
}
